import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled, { CSSProperties } from 'styled-components'
import Close from '../icons/Close'
import { RADOM_COLORS } from '../util/Constants'
import { IconButton } from './Button'
import Dropdown, { DropdownContentBody, DropdownContentHeader, DropdownTextInput } from './Dropdown'
import Spinner from './Spinner'

interface IProps<T> {
  itemArray: T[]
  selectedItems: T[]
  setSelectedItems: (items: T[]) => void
  keyExtractor: (item: T) => string
  labelExtractor: (item: T) => React.ReactNode
  placeholder?: string
  dropdownPlaceholder?: string
  disabled?: boolean
  onNewItem?: () => void
  hideSelectedContent?: boolean
  isSingleSelect?: boolean
  headers?: React.ReactElement[]
  isLoading?: boolean
  dropdownListItemStyle?: CSSProperties
  innerStyle?: CSSProperties
  dropdownMaxHeight?: number
}

const DropdownListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  cursor: pointer;

  :hover {
    background-color: ${RADOM_COLORS.GRAY_LIGHTEST};
  }
`

const DropdownSelectedItem = styled.div`
  display: flex;
  align-items: center;
  border-radius: 5px;
  gap: 10px;
  background-color: ${RADOM_COLORS.GRAY_LIGHTEST};
  padding: 8px;
`

const DropdownPlaceholder = styled.div`
  padding: 20px;
  color: ${RADOM_COLORS.GRAY_DARKER};
`

// ---------------------------------------------------------------------------------------------------------------------
const SearchMultiselect = <T extends unknown>(props: IProps<T>): React.ReactElement => {
  const [query, setQuery] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1)
  const dropdownRef = useRef<Dropdown>(null)
  const queryInputRef = useRef<HTMLInputElement>(null)
  const listboxIdRef = useRef(`search-list-${Math.random().toString(36).slice(2, 9)}`)

  const setDropdown = (open: boolean): void => dropdownRef.current?.toggleDropdown(open)

  const focusQueryInput = (): void => {
    queryInputRef.current?.focus()
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => queryInputRef.current?.focus())
    }
    setTimeout(() => queryInputRef.current?.focus(), 20)
  }

  const itemSearchFilter = (item: T): boolean => {
    const probe = props.keyExtractor(item).toLowerCase()
    return !props.selectedItems.find(i => props.keyExtractor(i).toLowerCase() === probe) &&
      (query === '' || probe.includes(query.toLowerCase()))
  }

  const dropdownItems = useMemo(
    () => props.itemArray.filter(itemSearchFilter),
    [props.itemArray, props.selectedItems, query]
  )

  const onQueryChange = (e: React.ChangeEvent): void => {
    const val = (e.target as HTMLInputElement).value
    if (val !== '') {
      setDropdown(true)
    }
    setQuery(val)
    setHighlightedIndex(0)
  }

  useEffect(() => {
    setHighlightedIndex((currentIndex) => {
      if (dropdownItems.length === 0) {
        return -1
      }

      if (currentIndex < 0) {
        return 0
      }

      if (currentIndex >= dropdownItems.length) {
        return dropdownItems.length - 1
      }

      return currentIndex
    })
  }, [dropdownItems.length])

  useEffect(() => {
    if (highlightedIndex < 0) {
      return
    }

    const activeItemId = `${listboxIdRef.current}-${highlightedIndex}`
    document.getElementById(activeItemId)?.scrollIntoView({ block: 'nearest' })
  }, [highlightedIndex])

  const onItemSelect = (e: React.MouseEvent | React.KeyboardEvent, item: T): void => {
    e.preventDefault()
    e.stopPropagation()

    setQuery('')
    setDropdown(false)
    setHighlightedIndex(-1)
    if (props.isSingleSelect) {
      props.setSelectedItems([item])
    } else {
      props.setSelectedItems([...props.selectedItems, item])
    }
  }

  const onItemDelete = (e: React.MouseEvent, item: T): void => {
    e.preventDefault()
    e.stopPropagation()

    props.setSelectedItems(
      props.selectedItems
        .filter(c => props.keyExtractor(c) !== props.keyExtractor(item))
    )
  }

  const onClickDropdown = (): void => focusQueryInput()
  const onDropdownWrapperKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.currentTarget !== e.target) {
      return
    }

    if (e.ctrlKey || e.metaKey || e.altKey) {
      return
    }

    if (e.key.length === 1) {
      e.preventDefault()
      setDropdown(true)
      setQuery(prev => `${prev}${e.key}`)
      setHighlightedIndex(0)
      focusQueryInput()
      return
    }

    if (e.key === 'Backspace' && query.length > 0) {
      e.preventDefault()
      setDropdown(true)
      setQuery(prev => prev.slice(0, -1))
      setHighlightedIndex(0)
      focusQueryInput()
    }
  }

  const onQueryKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (!dropdownItems.length) {
      if (e.key === 'Escape') {
        setDropdown(false)
      }
      if (e.key === 'Tab') {
        setDropdown(false)
      }
      return
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setDropdown(true)
      setHighlightedIndex((prev) => (prev + 1) % dropdownItems.length)
      return
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setDropdown(true)
      setHighlightedIndex((prev) => (prev <= 0 ? dropdownItems.length - 1 : prev - 1))
      return
    }

    if (e.key === 'Enter') {
      const item = dropdownItems[highlightedIndex >= 0 ? highlightedIndex : 0]
      if (item) {
        onItemSelect(e, item)
      }
      return
    }

    if (e.key === 'Escape') {
      setDropdown(false)
      setHighlightedIndex(-1)
      return
    }

    if (e.key === 'Tab') {
      setDropdown(false)
      setHighlightedIndex(-1)
    }
  }

  // -------------------------------------------------------------------------------------------------------------------
  return (
    <div style={{ fontSize: 14 }}>
      {
        <Dropdown
          ref={dropdownRef}
          onClick={onClickDropdown}
          onKeyDown={onDropdownWrapperKeyDown}
          openOnFocus={true}
          disabled={props.disabled}
          outerStyle={props.innerStyle}
          dropdownMaxHeight={props.dropdownMaxHeight ?? 320}

          // Format zero or more selected items
          selectedContent={
            <div style={{ width: '100%', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {
                props.isLoading && <Spinner />
              }
              {
                (props.selectedItems.length === 0 || props.hideSelectedContent) &&
                !props.isLoading &&
                <span style={{ opacity: 0.5 }}>{props.placeholder ?? 'Select items'}</span>
              }
              {
                props.selectedItems.length > 0 &&
                !props.hideSelectedContent &&
                !props.isLoading &&
                props.selectedItems.map((item, i) =>
                  props.isSingleSelect
                    ? <div style={{ display: 'flex' }} key={i}>
                      {props.labelExtractor(item)}
                    </div>
                    : <DropdownSelectedItem key={i}>
                      {props.labelExtractor(item)}
                      {
                        !props.disabled &&
                          <IconButton style={{ padding: 5 }} onClick={(e) => onItemDelete(e, item)}>
                            <Close style={{ width: 10, pointerEvents: 'none' }} />
                          </IconButton>
                      }
                    </DropdownSelectedItem>
                )
              }
            </div>
          }

          // Format list items
          dropdownContent={
            <div>
              <div style={{
                position: 'sticky',
                top: 0,
                background: 'white',
                zIndex: 1,
                boxShadow: `0 0 5px ${RADOM_COLORS.GRAY_DARK}`
              }}>
                <DropdownContentHeader
                  style={{ borderBottom: `1px solid ${RADOM_COLORS.GRAY_DARK}` }}>
                  <DropdownTextInput
                    ref={queryInputRef}
                    type="text"
                    autoComplete="off"
                    value={query}
                    onChange={onQueryChange}
                    onKeyDown={onQueryKeyDown}
                    onFocus={() => {
                      setDropdown(true)
                      focusQueryInput()
                    }}
                    placeholder={'Search...'}
                    role="combobox"
                    aria-autocomplete="list"
                    aria-controls={listboxIdRef.current}
                    aria-label={props.dropdownPlaceholder ?? 'Search options'}
                    aria-activedescendant={
                      highlightedIndex >= 0 ? `${listboxIdRef.current}-${highlightedIndex}` : undefined
                    }
                  />
                  {
                    query !== '' &&
                    <IconButton
                      style={{ position: 'absolute', right: 10, padding: 5 }}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setQuery('')
                      }}>
                      <Close style={{ width: 10, pointerEvents: 'none' }} />
                    </IconButton>
                  }
                </DropdownContentHeader>
                {
                  props.headers?.map((h, i) =>
                    <DropdownContentHeader onClick={() => setDropdown(false)} key={i}>
                      {h}
                    </DropdownContentHeader>
                  )
                }
              </div>

              <DropdownContentBody id={listboxIdRef.current} role="listbox">
                {
                  !dropdownItems.length &&
                  <DropdownPlaceholder>{props.dropdownPlaceholder ?? 'No Items'}</DropdownPlaceholder>
                }
                {
                  dropdownItems
                    .map((item, index) =>
                      <DropdownListItem
                        id={`${listboxIdRef.current}-${index}`}
                        key={props.keyExtractor(item)}
                        onClick={e => onItemSelect(e, item)}
                        style={{
                          ...(props.dropdownListItemStyle ?? {}),
                          backgroundColor: index === highlightedIndex ? RADOM_COLORS.GRAY_LIGHTEST : undefined
                        }}
                        role="option"
                        aria-selected={index === highlightedIndex}
                        tabIndex={-1}
                      >
                        {props.labelExtractor(item)}
                      </DropdownListItem>
                    )
                }
              </DropdownContentBody>
            </div>
          }
        />
      }
    </div>
  )
}

export default SearchMultiselect
